import ApiRequest from '@js/utilities/apiRequest.js'
import { url } from '@js/utilities/url.js'
import { AbiDataFiller } from '@js/utilities/abiDataFiller'
import { errorNotify } from '@js/utilities/bugsnag.js'

// lodash components
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import split from 'lodash/split'
import startsWith from 'lodash/startsWith'

async function getAppData(store, ref) {
    let path = new URLSearchParams(document.location.search)
    let params = {}

    for (const [key, value] of path.entries()) {
        params[key] = value
    }

    return ApiRequest.get(store, url(`/api/v2/app-data/${ref}`), params)
}

async function fetchGlobalData(store, pageUrl = null) {
    let path = pageUrl || window.location.pathname
    let params = {}
    const apiPath = url('/api/v2/global')
    const paramString = path.split('?')

    if (paramString) {
        params = Object.fromEntries(new URLSearchParams(paramString[1]))
        path = paramString[0]
    }
    params.pagePath = path

    return ApiRequest.get(store, apiPath, params)
}

async function setJourneyError(store, i18n) {
    store.commit('showErrorPage', {
        title: i18n.global.t('errors.pages.clientError.heading'),
        description: i18n.global.t('errors.pages.clientError.description'),
    })

    console.error('Journey data empty')
    errorNotify('Journey data empty', 'error', 'Apply')
}

async function setGlobalData(to, store, i18n, translationsPromise, router, emitter) {
    let uri = to ? to.fullPath : null

    if (uri) {
        uri = split(uri, '#')[0]
    }

    // load global data before translations finishes
    const globalData = await fetchGlobalData(store, uri)

    // make sure to only navigate once translations are loaded (fired off earlier)
    if (!(await translationsPromise)) {
        store.commit('showErrorPage', {
            title: i18n.global.t('errors.pages.clientError.heading'),
            description: i18n.global.t('errors.pages.clientError.description'),
        })

        store.commit('setGeneralLoaderVisible', false)

        return false
    }

    store.commit('setGeneralLoaderVisible', false)

    // catch main page error but still let global data populate below if available for error page header, etc
    if (window.HTTP_STATUS && window.HTTP_STATUS !== 200) {
        store.dispatch('processErrorHttpStatus', window.HTTP_STATUS, true)
    }

    const currentRouteURL = uri.substring(1)
    const errorCodesToCheck = [404, 403, 422, 500]

    const isErrorInURL = errorCodesToCheck.some(errorCode => {
        const regex = new RegExp(`^${errorCode}$`)
        return regex.test(currentRouteURL)
    })

    if (isErrorInURL) {
        store.dispatch('processErrorHttpStatus', parseInt(currentRouteURL), true)
    }

    if (globalData) {
        if (globalData.page.journey) {
            store.commit('setJourneyData', { journey: globalData.page.journey, router, store })

            if (!globalData.page.journey?.data?.length) {
                setJourneyError(store, i18n)
            }
        } else if (startsWith(uri, '/apply')) {
            store.state.apply.applyRoutesLoaded = true
            setJourneyError(store, i18n)
        }

        if (globalData.global) {
            forEach(globalData.global, (data, key) => {
                store.state.global.data[key] = data
            })

            store.commit('setLoanRanges', store.getters.loanRange)
        }

        new AbiDataFiller(globalData.global, i18n, store)

        if (!isEmpty(globalData.page)) {
            store.state.global.page = globalData.page

            emitter.emit('meta:title', globalData.page.title)
            emitter.emit('meta:description', globalData.page.description)
            window.CAMPAIGN_CODE = globalData.page.campaign_code
        } else {
            store.state.global.page = null
            store.dispatch('processErrorHttpStatus', 404, false)
        }
    }

    return true
}

export { getAppData, setGlobalData }
